import { z } from "zod";

// Create accepts the full address; the backend PUT /locations/{id} only accepts
// { name } (see UpdateLocationRequest in models/location.ts), so in edit mode the
// address fields are read-only.
// TODO(backend): editable per-location address (PUT only supports renaming today).
export const createLocationSchema = z.object({
    name: z.string().min(1,"Name is required"),
    streetAddress: z.string().min(1,"Street address is required"),
    city: z.string().min(1,"City is required"),
    // Must be the 2-letter USPS code, uppercase. LegalClassRestriction rows are
    // stored that way and matched exactly, and §3.12 treats "no match" as
    // allowed — so a full state name here would silently disable the
    // restriction rather than fail loudly. Mirrors the /apply rule (#191).
    state: z
        .string()
        .nonempty("State is required")
        .regex(/^[A-Z]{2}$/, "Select a state from the list"),
    // §3.12 evaluates by state AND zip, so a malformed zip fails open the same
    // way a free-text state does — it matches no restriction row, and "no match"
    // reads as allowed. ZIP+4 is deliberately accepted and posted as entered:
    // the API's NormalizeZipCode reduces it to the 5-digit prefix at match time,
    // so stripping it here would only mask a future divergence between the two
    // normalizers. Mirrors the /apply rule (#191) and the server rule.
    zipCode: z
        .string()
        .trim()
        .nonempty("Zip code is required")
        .regex(
            /^\d{5}(-\d{4})?$/,
            "Enter a 5-digit ZIP, optionally ZIP+4",
        ),
    country: z.string().min(1,"Country is required"),
});

// Edit submits { name } and nothing else, so only the name is validated. The
// address fields are disabled here and carry whatever was persisted — including
// a pre-#325 free-text state like "New York" or a malformed zip. Validating
// them would block a rename on fields the user has no way to correct.
export const editLocationSchema = z.object({
    name: z.string().min(1,"Name is required"),
    streetAddress: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    country: z.string(),
});

export type LocationFormData = z.infer<typeof createLocationSchema>;
